import React, { useEffect } from 'react';
import { PieceType } from '../../services/zodiacEngine/types';
import { allPieceTypes } from '../../services/zodiacEngine/const';
import styles from './PieceSelection.module.css';

interface PieceSelectionPanelProps {
  placedPieces: Set<string>;
  selectedKing: PieceType | null;
  isLight: boolean;
  onPieceDragStart: (e: React.DragEvent<HTMLImageElement>, pieceType: PieceType, isLight: boolean) => void;
  hideKingIndicator?: boolean; // Hide crown for local PvP (fog of war)
  compact?: boolean; // Compact layout for side-by-side display (Local 2P)
}

export const PieceSelectionPanel: React.FC<PieceSelectionPanelProps> = ({
  placedPieces,
  selectedKing,
  isLight,
  onPieceDragStart,
  hideKingIndicator = false,
  compact = false,
}) => {
  const sideSuffix = isLight ? 'light' : 'dark';

  const placedCountForSide = React.useMemo(() => {
    let count = 0;
    for (const key of placedPieces) {
      if (key.endsWith(`-${sideSuffix}`)) count++;
    }
    return count;
  }, [placedPieces, sideSuffix]);

  const kingKey = selectedKing !== null ? `${selectedKing}-${sideSuffix}` : null;
  const kingIsPlaced = kingKey !== null ? placedPieces.has(kingKey) : false;

  // In local PvP (hideKingIndicator), don't force king placement to avoid revealing which piece is the king
  const mustPlaceKing =
    !hideKingIndicator &&
    selectedKing !== null &&
    placedCountForSide === 4 &&
    !kingIsPlaced;

  React.useEffect(() => {
    // Don't show warning in local PvP mode - players can place king any time
    if (!selectedKing || hideKingIndicator) return;

    if (placedCountForSide === 4 && !kingIsPlaced) {
    }
  }, [placedCountForSide, selectedKing, kingIsPlaced, hideKingIndicator]);

  // ✅ Decide rendering AFTER hooks
  const shouldHidePanel = placedCountForSide >= 5;
  if (shouldHidePanel) return null;

  const pieces = allPieceTypes.map((pieceType) => {
    const pieceKey = `${pieceType}-${sideSuffix}`;
    const isPlaced = placedPieces.has(pieceKey);
    const isKing = pieceType === selectedKing;

    // Piece is disabled if already placed OR if must place king and this isn't the king
    const isDisabled = isPlaced || (mustPlaceKing && !isKing);

    return (
      <div
        key={pieceKey}
        className={`${styles.pieceIconContainer} ${isDisabled ? styles.pieceDisabled : ''}`}
      >
        <div className={styles.pieceIconWrapper}>
          {isKing && !hideKingIndicator ? (
            <div className={styles.kingPieceContainer}>
              <img
                src={`/Icons/${pieceType}-${isLight ? 'light' : 'dark'}.png`}
                alt={`${pieceType}-${isLight ? 'light' : 'dark'}`}
                className={styles.pieceIcon}
                draggable={!isDisabled}
                onDragStart={(e) => !isDisabled && onPieceDragStart(e, pieceType, isLight)}
              />
              <img src="/Icons/crown1.png" alt="King Crown" className={styles.paletteCrownOverlay} />
            </div>
          ) : (
            <img
              src={`/Icons/${pieceType}-${isLight ? 'light' : 'dark'}.png`}
              alt={`${pieceType}-${isLight ? 'light' : 'dark'}`}
              className={styles.pieceIcon}
              draggable={!isDisabled}
              onDragStart={(e) => !isDisabled && onPieceDragStart(e, pieceType, isLight)}
            />
          )}
          <span className={styles.pieceName}>{pieceType}</span>
        </div>
      </div>
    );
  });

  const containerClass = compact
    ? `${styles.pieceIcons} ${styles.pieceIconsCompact}`
    : styles.pieceIcons;

  return <div className={containerClass}>{pieces}</div>;
};
